import { searchPlaces } from "@healthshare/shared/commands/places"
import type { BackendApi } from "@healthshare/shared/backend/client"

/**
 * Proxies through POST /backend/places/search rather than calling
 * places.googleapis.com directly, so the API key stays server-side instead
 * of shipping in the public web bundle. The field mask is fixed server-side
 * too, since it drives per-call billing.
 */
export async function searchPlacesNew(
  api: BackendApi,
  query: string,
  options: { limit: number } = { limit: 10 },
): Promise<GooglePlacesSearchResponse> {
  // The field mask restricts Google's response to 5 fields (openapi.yaml's
  // `GooglePlace` schema), so this type -- which predates the mask and
  // describes the full Places API shape -- has always been a cast rather
  // than a fact about the payload. Preserved as-is; narrowing it is a
  // separate cleanup.
  const result = await searchPlaces(api, query, { limit: options.limit || 10 })
  return result as unknown as GooglePlacesSearchResponse
}

interface GooglePlacesSearchResponse {
  places: GooglePlaceResult[]
}

export interface GooglePlaceResult {
  name: string
  id: string
  types: string[]
  nationalPhoneNumber: string
  internationalPhoneNumber: string
  formattedAddress: string
  addressComponents: AddressComponent[]
  plusCode: {
    globalCode: string
    compoundCode: string
  }
  location: {
    latitude: number
    longitude: number
  }
  viewport: {
    low: {
      latitude: number
      longitude: number
    }
    high: {
      latitude: number
      longitude: number
    }
  }
  rating: number
  googleMapsUri: string
  websiteUri: string
  regularOpeningHours: {
    openNow: boolean
    periods: Hours[]
  }
  utcOffsetMinutes: number
  adrFormatAddress: string
  businessStatus: string
  userRatingCount: number
  iconMaskBaseUri: string
  iconBackgroundColor: string
  displayName: {
    text: string
    languageCode: string
  }
  primaryTypeDisplayName: {
    text: string
    languageCode: string
  }
  delivery: boolean
  currentOpeningHours: {
    openNow: boolean
    periods: Hours[]
  }
  currentSecondaryOpeningHours: {
    openNow: boolean
    periods: Hours[]
  }
  regularSecondaryOpeningHours: {
    openNow: boolean
    periods: Hours[]
  }
  primaryType: string
  shortFormattedAddress: string
  
  
  paymentOptions?: {
    acceptsCreditCards: boolean
    acceptsDebitCards: boolean
    acceptsCashOnly: boolean
  },
  accessibilityOptions?: {
    wheelchairAccessibleParking: boolean
    wheelchairAccessibleEntrance: boolean
  },
  addressDescriptor?: {
    landmarks?: Landmark[]
    areas: Area[]
  },
  googleMapsLinks?: {
    directionsUri: string
    placeUri: string
    writeAReviewUri: string
    reviewsUri: string
    photosUri: string
  }
}

interface Landmark {
  name: string
  placeId: string
  displayName: {
    text: string
    languageCode: string
  }
  types: string[]
}

interface Area {
  name: string
  placeId: string
  displayName: {
    text: string
    languageCode: string
  }
  containment: string
}

interface AddressComponent {
  longText: string
  shortText: string
  types: string[]
  languageCode: string
}

interface Hours {
  open: {
    day: number
    hour: number
    minute: number
    date: {
      year: number
      month: number
      day: number
    }
  }
  close: {
    day: number
    hour: number
    minute: number
    date: {
      year: number
      month: number
      day: number
    }
  }
}
